import { ReactComponent as WastebasketIcon } from '../../assets/wastebasket.svg';
import { ReactComponent as EditIcon } from '../../assets/editIcon.svg';
import styles from './SingleEventCard.module.scss';
import CustomTypography from '../customTypography/CustomTypography';
import { ReactComponent as EventIconWIW } from '../../assets/warsaw_industry_week.svg';
import { ReactComponent as EventIconIBW } from '../../assets/industrial_bulding_week.svg';
import { getBrandingFileUrl } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { ReactComponent as ProgressIcon21 } from '../../assets/21%.svg';
import { ReactComponent as ProgressIcon65 } from '../../assets/65%.svg';
import { Box } from '@mui/material';
import { useState, useCallback, useEffect } from 'react';
import { getBrandingFiles, deleteExhibition } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import ConfirmationDialog from '../confirmationDialog/ConfirmationDialog';

interface SingleEventCardProps {
  id: number;
  exhibitorId?: number;
  title: string;
  start_date: string;
  end_date: string;
  // Stoisko: gdy firma ma więcej niż jedno stoisko na tym wydarzeniu, kafelki różnią się
  // halą i numerem, a operacje muszą dotyczyć konkretnego stoiska.
  participationId?: number | undefined;
  standLabel?: string | undefined;
  handleSelectEvent?: (id: number, participationId?: number) => void;
  handleDeleteEventFromExhibitor?: (id: number, exhibitorId: number, participationId?: number) => void;
  iconId: number;
  event_readiness?: number;
  showDelete?: boolean;   
  showSelect?: boolean;  
  showEdit?: boolean;
  eventLogoFileName?: string | null;
  preferTileLogo?: boolean; // prefer logo_kolowe_tlo_kafel when available
}

const SingleEventCard: React.FC<SingleEventCardProps> = ({
  id,
  title,
  start_date,
  end_date,
  exhibitorId,
  participationId,
  standLabel,
  iconId,
  event_readiness = 0, // domyślnie brak postępu
  handleSelectEvent,
  handleDeleteEventFromExhibitor,
  showDelete = true,
  showSelect = true,
  showEdit = false,
  eventLogoFileName,
  preferTileLogo = true,
}) => {
  const [openConfirm, setOpenConfirm] = useState(false);
  const [openDeleteExhibitionConfirm, setOpenDeleteExhibitionConfirm] = useState(false);
  const navigate = useNavigate();
  const { token } = useAuth();
  const [resolvedLogoUrl, setResolvedLogoUrl] = useState<string | null>(null);

  const formatDateRange = useCallback((startDate: string, endDate: string): string => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    const startFormatted = start.toLocaleDateString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    const endFormatted = end.toLocaleDateString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

    return `${startFormatted}-${endFormatted}`;
  }, []);

  const renderIcon = (iconId: number) => {
    switch (iconId) {
      case 1:
        return <EventIconWIW className={styles.logo} />;
      case 2:
        return <EventIconIBW className={styles.logo} />;
      default:
        return null;
    }
  };

  const renderProgressIcon = (progress: number) => {
    switch (progress) {
      case 21:
        return <ProgressIcon21 className={styles.progressIcon} />;
      case 65:
        return <ProgressIcon65 className={styles.progressIcon} />;
      default:
        return null;
    }
  };

  const handleConfirmDelete = () => {
    if (handleDeleteEventFromExhibitor && exhibitorId !== undefined) {
      handleDeleteEventFromExhibitor(id, exhibitorId, participationId);
    }
    setOpenConfirm(false);
  };

  const handleConfirmDeleteExhibition = async () => {
    try {
      if (!token) return;
      await deleteExhibition(id, token);
      // Navigate to main dashboard after successful deletion
      navigate('/dashboard');
    } catch (e: any) {
      alert(e?.message || 'Nie udało się usunąć wydarzenia');
    } finally {
      setOpenDeleteExhibitionConfirm(false);
    }
  };

  // Resolve event logo dynamically
  useEffect(() => {
    const resolveLogo = async () => {
      try {
        if (!token) {
          setResolvedLogoUrl(null);
          return;
        }
        // 1) Prefer exhibitor-scoped branding for this event
        if (typeof exhibitorId === 'number') {
          try {
            const filesResp = await getBrandingFiles(exhibitorId, id, token);
            const files = filesResp.files || {};
            const preferredKey = preferTileLogo
              ? (files['logo_kolowe_tlo_kafel']
                  ? 'logo_kolowe_tlo_kafel'
                  : (files['event_logo']
                      ? 'event_logo'
                      : (Object.keys(files).find(k => k.toLowerCase().includes('logo'))
                          || Object.keys(files)[0])))
              : (files['event_logo']
                  ? 'event_logo'
                  : (Object.keys(files).find(k => k.toLowerCase().includes('logo'))
                      || Object.keys(files)[0]));
            if (preferredKey && files[preferredKey]) {
              const value: any = files[preferredKey] as any;
              const file = Array.isArray(value) ? value[0] : value;
              if (file?.fileName) {
                setResolvedLogoUrl(getBrandingFileUrl(exhibitorId, file.fileName, token));
                return;
              }
            }
          } catch {
            // ignore, try global
          }
        }
        // 2) Fall back to global (prefer event_logo when preferTileLogo is false)
        try {
          const g = await getBrandingFiles(null, id, token);
          const globalKey = g.files && (preferTileLogo
            ? (g.files['logo_kolowe_tlo_kafel'] ? 'logo_kolowe_tlo_kafel' : (g.files['event_logo'] ? 'event_logo' : null))
            : (g.files['event_logo'] ? 'event_logo' : null));
          if (globalKey) {
            const value: any = g.files[globalKey] as any;
            const file = Array.isArray(value) ? value[0] : value;
            if (file?.fileName) {
              setResolvedLogoUrl(getBrandingFileUrl(null, file.fileName, token));
              return;
            }
          }
        } catch {
          // ignore
        }
        setResolvedLogoUrl(null);
      } catch {
        setResolvedLogoUrl(null);
      }
    };
    void resolveLogo();
  }, [token, id, exhibitorId, preferTileLogo]);

  return (
    <>
      <Box className={`${styles.eventCardContainer} ${token && eventLogoFileName ? styles.hasLogoCard : ''} ${showEdit ? styles.autoHeight : ''}`}>
        <Box className={styles.deleteIconContainer}>
          {showDelete && handleDeleteEventFromExhibitor && exhibitorId !== undefined ? (
            <Box 
              style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}
              onClick={() => setOpenConfirm(true)}
              title="Usuń przypisanie"
            >
              <WastebasketIcon className={styles.wastebasketIcon} />
              <CustomTypography className={styles.editEventText}>usuń przypisanie</CustomTypography>
            </Box>
          ) : (
            <Box className={styles.wastebasketIcon} /> 
          )}
        </Box>

        <Box className={styles.container}>
          <Box className={`${styles.eventLogo} ${(token && (resolvedLogoUrl || eventLogoFileName)) ? styles.hasLogo : ''}`}>
            {token && (resolvedLogoUrl || eventLogoFileName) ? (
              <img
                src={resolvedLogoUrl || getBrandingFileUrl(null, eventLogoFileName as string, token)}
                alt={`${title} logo`}
                className={styles.logo}
              />
            ) : renderIcon(iconId)}
          </Box>
          <Box className={styles.eventInfo}>
            <Box className={styles.dateInfo}>{formatDateRange(start_date, end_date)}</Box>
            <Box className={styles.eventTitle}>{title || ""}</Box>
            {standLabel ? <Box className={styles.dateInfo}>{standLabel}</Box> : null}
          </Box>
        </Box>

        {showEdit && (
          <>
            <Box className={styles.editBox}>
              <Box className={styles.fieldLabelContainer} >
                <CustomTypography className={styles.fieldLabel}>Branża:</CustomTypography>
              </Box>

              <Box className={styles.editInfo}>
                <CustomTypography className={styles.fieldValue}>Dom</CustomTypography>
                <Box className={styles.actionEditButton}>
                  <Box 
                    className={styles.boxWithHover}
                    onClick={() => {
                      const evt = new CustomEvent('open-edit-event-modal', { detail: { id, title, start_date, end_date, participationId } });
                      window.dispatchEvent(evt);
                    }}
                  > 
                    <EditIcon className={styles.editEvent}/>
                    <CustomTypography className={styles.editEventText}>edytuj</CustomTypography>
                  </Box>
                  <Box 
                    className={styles.boxWithHover}
                    onClick={() => setOpenDeleteExhibitionConfirm(true)}
                    style={{ marginLeft: '12px' }}
                    title="Usuń wystawę"
                  > 
                    <WastebasketIcon className={styles.editEvent}/>
                    <CustomTypography className={styles.editEventText}>usuń wystawę</CustomTypography>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box className={styles.editBoxCompact}>
              <Box className={styles.fieldLabelContainer} >
                <CustomTypography className={styles.fieldLabel}>ID wydarzenia:</CustomTypography>
              </Box>

              <Box className={styles.editInfo}>
                <CustomTypography className={styles.fieldValue}>{id}</CustomTypography>
              </Box>
            </Box>
          </>
        )}
        <Box className={styles.actionInfo}>
          <Box className={styles.readyInfo}>
            <CustomTypography className={styles.readyText}>Gotowość:</CustomTypography>
            {renderProgressIcon(event_readiness)}
          </Box>
          <Box className={styles.action}>
            {showSelect && handleSelectEvent ? (
              <Box
                className={styles.actionButton}
                onClick={() => handleSelectEvent(id, participationId)}
              >
                <CustomTypography className={styles.chooseText}>wybierz</CustomTypography>
              </Box>
            ) : (
              <Box className={styles.actionButton} />  
            )}
          </Box>
        </Box>
      </Box>

      <ConfirmationDialog
        open={openConfirm}
        onClose={() => setOpenConfirm(false)}
        onConfirm={handleConfirmDelete}
        title="Odłącz wydarzenie od wystawcy"
        description="Czy na pewno chcesz odłączyć to wydarzenie od tego wystawcy?"
      />
      <ConfirmationDialog
        open={openDeleteExhibitionConfirm}
        onClose={() => setOpenDeleteExhibitionConfirm(false)}
        onConfirm={handleConfirmDeleteExhibition}
        title="Usuń wydarzenie"
        description="Czy na pewno chcesz trwale usunąć to wydarzenie wraz ze wszystkimi danymi?"
      />
    </>
  );
};
export default SingleEventCard;